import { EventEmitter, Injectable, signal } from "@angular/core";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { catchError, EMPTY, Observable, of, switchMap, take, throwError } from "rxjs";
import { TranslationService } from "@core-services/translation.service";
import { ToastrService } from "ngx-toastr";
import {
	AgreementBundle,
	TERMS_API_BASE_URL_PATH,
	TERMS_API_GET_BUNDLES_PATH,
	TERMS_API_SAVE_AGREEMENT_PATH,
	UserJourney
} from "../entities/terms-and-conditions.model";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { convertObjectToArrayIfValTrue } from "@modules/trade/helpers/helper";
import { tap } from "rxjs/operators";

@Injectable({
	providedIn: "root"
})
export class TermsAndConditionsService {
	public isDynamictermAndConditionActive$ = this.launchDarklyService.isActive$(LD_FLAG.TERMS_AND_CONDITIONS);
	public onTermsAcceptedSuccess: EventEmitter<UserJourney[]> = new EventEmitter<UserJourney[]>();
	public bundlesCache = new Map<string, AgreementBundle[]>();

	hasBundlesError$ = signal(false);
	hasBundlesError$$ = this.hasBundlesError$.asReadonly();

	constructor(
		private readonly apiCallService: ApicallService,
		private readonly translationService: TranslationService,
		private readonly toastr: ToastrService,
		private readonly launchDarklyService: LaunchDarklyService
	) {}

	public saveTerms(userJourneys: UserJourney[], bundleVersionIds: string[]): Observable<null> {
		const params = {
			user_journeys: userJourneys,
			agreement_bundle_version_ids: bundleVersionIds
		};
		const userJourneysString = this.getUserJourneyString(userJourneys);
		const options = new ApicallOption(null, params);
		options.hasNullResponse = true;

		if (this.bundlesCache.has(userJourneysString)) {
			this.bundlesCache.delete(userJourneysString);
		}

		return this.apiCallService.put(TERMS_API_BASE_URL_PATH + TERMS_API_SAVE_AGREEMENT_PATH, options);
	}

	private getUserJourneyString(userJourney: UserJourney | UserJourney[]): string {
		if (Array.isArray(userJourney)) {
			return userJourney.sort().join(",");
		}
		return userJourney;
	}

	public getBundlesRemote(
		userJourney: UserJourney | UserJourney[],
		shouldReturnError: boolean = false
	): Observable<AgreementBundle[]> {
		const userJourneyString = this.getUserJourneyString(userJourney);

		if (this.bundlesCache.has(userJourneyString) && userJourney !== UserJourney.ChangesNotification) {
			return of(this.bundlesCache.get(userJourneyString));
		}

		const options = new ApicallOption(null, {});
		options.raw = true;

		const getTermsQueryParams = `?user_journey=${userJourneyString}`;
		const getTermsUrl = TERMS_API_BASE_URL_PATH + TERMS_API_GET_BUNDLES_PATH + getTermsQueryParams;

		this.hasBundlesError$.set(false);

		return this.apiCallService.get(getTermsUrl, options).pipe(
			tap((data) => {
				this.bundlesCache.set(userJourneyString, data);
				this.hasBundlesError$.set(false);
			}),
			catchError((err) => {
				if (shouldReturnError) {
					return throwError(() => err);
				}
				this.showToastrError();
				this.hasBundlesError$.set(true);
				return of([]);
			})
		);
	}

	public getBundlesRemoteErrorUnhandled(userJourney: UserJourney | UserJourney[]): Observable<AgreementBundle[]> {
		const options = new ApicallOption(null, {});
		options.raw = true;
		const userJourneyString = this.getUserJourneyString(userJourney);
		const getTermsQueryParams = `?user_journey=${userJourneyString}`;
		const getTermsUrl = TERMS_API_BASE_URL_PATH + TERMS_API_GET_BUNDLES_PATH + getTermsQueryParams;

		return this.apiCallService.get(getTermsUrl, options);
	}

	public acceptTerms(
		userJourneys: UserJourney[],
		formValue: { [key: string]: boolean },
		onReadyCallback: Function
	): void {
		const acceptedBundleVersionIds = convertObjectToArrayIfValTrue(formValue);
		this.saveTerms(userJourneys, acceptedBundleVersionIds).subscribe({
			next: () => {
				onReadyCallback();
				this.onTermsAcceptedSuccess.emit(userJourneys);
			},
			error: () => {
				this.showToastrError();
			}
		});
	}

	// get agreement bundles
	public getAgreementBundleData$(
		userJourney: UserJourney | UserJourney[],
		shouldReturnError: boolean = false
	): Observable<AgreementBundle[]> {
		return this.isDynamictermAndConditionActive$.pipe(
			switchMap((isActive) => {
				if (isActive) {
					return this.getBundlesRemote(userJourney, shouldReturnError);
				}
				return of([]);
			}),
			catchError((error) => {
				return throwError(() => error);
			})
		);
	}

	public checkTCaccepted(callback: Function): void {
		this.launchDarklyService
			.isActive$(LD_FLAG.TERMS_AND_CONDITIONS)
			.pipe(
				take(1),
				switchMap((isActive) => (isActive ? this.getBundlesRemote(UserJourney.ChangesNotification) : EMPTY))
			)
			.subscribe({
				next: (bundles: AgreementBundle[]) => {
					if (bundles.length > 0) {
						callback(bundles);
					} else {
						return;
					}
				},
				error: () => {
					this.showToastrError();
				}
			});
	}

	public showToastrError(): void {
		const errorMessage = this.translationService.translate("anti_phishing_screen_notification_error_description");
		const errorTitle = this.translationService.translate("anti_phishing_screen_notification_error_title");
		this.toastr.error(errorMessage, errorTitle);
	}
}
